import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  GridApiPremium,
  GridRowSelectionModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import Actions from '../../store/Actions';
import {
  PortfolioDataResponse,
  PortfolioUpdateRequest,
  PortfolioDataTable,
} from '../../types/generatedTypes';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';
import { useGroups } from '../../hooks/useGroups';
import { useGridSelectionHandler } from '../../components/Groups/useGridSelection';
import { useExpandGroups } from '../../components/Groups/groupUtils';

interface UseCustodianDataProps {
  apiRef: React.RefObject<GridApiPremium | null>;
  displayData: PortfolioDataTable[];
  syncPortfolioCustodianFromIllio: (
    refreshCallback: () => Promise<void>
  ) => Promise<void>;
}

export const useCustodianData = ({
  apiRef,
  displayData,
  syncPortfolioCustodianFromIllio,
}: UseCustodianDataProps) => {
  const [custodianData, setCustodianData] = useState<PortfolioDataTable[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [autosizeColumns, setAutosizeColumns] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  // Groups functionality
  const {
    groups,
    setGroups,
    isGroupingEnabled,
    setIsGroupingEnabled,
    isGroupsModalOpen,
    loadGroupsFromBackend,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  } = useGroups({ pageType: 'custodian' });

  // Column fields configuration
  const columnFields = useMemo(
    () => [
      'client_name',
      'account_number',
      'upload_transactions_enabled',
      'portfolio_name',
      'portfolio_id',
      'custodian_name',
      'custodian_id',
      'leading_currency',
    ],
    []
  );

  const fetchCustodianData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await Actions.getPortfolioData();
      const dataWithIds: PortfolioDataTable[] = data.map(
        (custodian: PortfolioDataResponse) => ({
          ...custodian,
          _id: custodian.id, // Use id as _id for DataGrid
        })
      );
      setCustodianData(dataWithIds);
    } catch (error) {
      console.error('Error fetching custodian data:', error);
      showSnackbar({
        title: 'Fetch Failed',
        type: SnackbarType.error,
        message: 'Failed to fetch custodian data',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCustodianRecord = useCallback(
    async (
      newRow: PortfolioDataTable,
      oldRow: PortfolioDataTable
    ): Promise<PortfolioDataTable> => {
      try {
        if (!newRow.id) {
          throw new Error('No MongoDB ID found for record');
        }

        // Determine what fields have changed
        const changedFields: PortfolioUpdateRequest = {};
        const editableFields: (keyof PortfolioUpdateRequest)[] = [
          'account_number',
        ];

        for (const field of editableFields) {
          if (newRow[field] !== oldRow[field]) {
            changedFields[field] = newRow[field];
          }
        }

        if (Object.keys(changedFields).length === 0) {
          // No changes detected
          return newRow;
        }

        // Update via API
        await Actions.updateCustodianData(newRow.id, changedFields);

        // Update local state
        setCustodianData(prevData =>
          prevData.map(item => (item.id === newRow.id ? newRow : item))
        );

        return newRow;
      } catch (error) {
        console.error('Error updating custodian record:', error);
        showSnackbar({
          title: 'Update Failed',
          type: SnackbarType.error,
          message:
            error instanceof Error
              ? error.message
              : 'Failed to update custodian record',
        });
        // Return old row to revert changes in UI
        return oldRow;
      }
    },
    []
  );

  const handleToggleUploadTransactions = useCallback(
    async (portfolioId: string, enabled: boolean) => {
      try {
        await Actions.updateCustodianData(portfolioId, {
          upload_transactions_enabled: enabled,
        });

        // Optimistic update of local state
        setCustodianData(prevData =>
          prevData.map(item =>
            item.id === portfolioId
              ? { ...item, upload_transactions_enabled: enabled }
              : item
          )
        );

        showSnackbar({
          title: 'Updated',
          type: SnackbarType.success,
          message: `Upload transactions ${enabled ? 'enabled' : 'disabled'}`,
        });
      } catch (error) {
        console.error('Failed to update upload transactions enabled:', error);
        showSnackbar({
          title: 'Update Failed',
          type: SnackbarType.error,
          message:
            error instanceof Error
              ? error.message
              : 'Failed to update upload transactions setting',
        });
      }
    },
    []
  );

  // Handle autosize state changes
  useEffect(() => {
    if (autosizeColumns && apiRef.current) {
      apiRef.current.autosizeColumns({
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
        columns: columnFields,
      });
    } else if (!autosizeColumns && apiRef.current) {
      // Reset column widths to allow flex to work
      columnFields.forEach(field => {
        apiRef.current?.setColumnWidth(field, 0);
      });
    }
  }, [autosizeColumns, apiRef, columnFields]);

  // Export data handler
  const handleExport = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv();
    }
  }, [apiRef]);

  // Sync handler wrapper
  const handleSyncPortfolioCustodianFromIllio = useCallback(async () => {
    await syncPortfolioCustodianFromIllio(fetchCustodianData);
  }, [syncPortfolioCustodianFromIllio, fetchCustodianData]);

  // Create expandGroups wrapper with current context
  const expandGroups = useExpandGroups(groups, displayData);

  // Row selection handler using shared utility
  const handleRowSelectionChange = useGridSelectionHandler(
    selectedRows,
    setSelectedRows,
    displayData,
    {
      getRowId: row => (row._id || row.id).toString(),
      expandGroups,
    }
  );

  // Apply column visibility
  const applyColumnVisibility = useCallback(
    (visibilityModel: GridColumnVisibilityModel) => {
      if (apiRef.current) {
        apiRef.current.setColumnVisibilityModel(visibilityModel);
      }
      setColumnVisibilityModel(visibilityModel);
    },
    [apiRef]
  );

  // Prepare available rows for groups modal
  const availableRowsForGroups = useMemo(
    () =>
      custodianData.map(row => ({
        id: row._id,
        label: `${row.client_name || 'Unknown'} - ${row.custodian_name || 'No Custodian'}`,
        client_name: row.client_name,
        custodian_name: row.custodian_name,
        account_number: row.account_number,
        portfolio_name: row.portfolio_name,
        portfolio_id: row.portfolio_id,
        custodian_id: row.custodian_id,
        leading_currency: row.leading_currency,
      })),
    [custodianData]
  );

  return {
    custodianData,
    loading,
    selectedRows,
    autosizeColumns,
    setAutosizeColumns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    applyColumnVisibility,
    fetchCustodianData,
    updateCustodianRecord,
    handleToggleUploadTransactions,
    handleExport,
    handleSyncPortfolioCustodianFromIllio,
    handleRowSelectionChange,
    expandGroups,
    // Groups
    groups,
    setGroups,
    loadGroupsFromBackend,
    isGroupsModalOpen,
    isGroupingEnabled,
    setIsGroupingEnabled,
    availableRowsForGroups,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  };
};
